import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Trophy, Users, User, CalendarDays, BarChart2, Loader2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import useStore from '../../store/useStore';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../components/ui/use-toast';
import { useMobileDetection } from '../hooks/useMobileDetection';

const MobileHomePage = () => {
  const {
    tournaments,
    teams,
    players,
    matches,
    fetchTournaments,
    fetchTeams,
    fetchPlayers,
    fetchMatches,
    loading
  } = useStore();

  const { user } = useAuth();
  const { orientation } = useMobileDetection();

  useEffect(() => {
    // Only fetch data if user is logged in
    if (user) {
      fetchTournaments();
      fetchTeams();
      fetchPlayers();
      fetchMatches();
    }
  }, [user, fetchTournaments, fetchTeams, fetchPlayers, fetchMatches]);

  const stats = [
    {
      title: 'Tournaments',
      value: tournaments.length,
      icon: Trophy,
      color: 'bg-cricket-green text-white',
      path: '/tournaments'
    },
    {
      title: 'Teams',
      value: teams.length,
      icon: Users,
      color: 'bg-cricket-green-light text-white',
      path: '/teams'
    },
    {
      title: 'Players',
      value: players.length,
      icon: User,
      color: 'bg-cricket-boundary text-white',
      path: '/players'
    },
    {
      title: 'Matches',
      value: matches.length,
      icon: CalendarDays,
      color: 'bg-cricket-accent text-white',
      path: '/matches'
    }
  ];

  if (loading.tournaments || loading.teams || loading.matches || loading.players) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-6 pb-20">
        {/* Hero Section */}
        <section className="relative overflow-hidden py-8 bg-gradient-to-br from-primary to-green-700 text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl font-extrabold mb-4 drop-shadow-lg">
              Cricket Scoring System
            </h1>
            <p className="text-lg max-w-2xl mx-auto mb-6 opacity-90 leading-relaxed">
              A modern, intuitive platform for managing cricket tournaments, teams, and matches with live updates and in-depth statistics.
            </p>
            <Link to="/auth">
              <Button className="bg-accent text-accent-foreground px-8 py-4 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
                Get Started Now
              </Button>
            </Link>
            <p className="text-sm opacity-80 mt-3">Sign in or create an account to unlock full features.</p>
          </div>
        </section>

        {/* Features Grid */}
        <section className="container mx-auto px-4 py-8">
          <h2 className="text-2xl font-bold mb-6 text-center">Key Features</h2>
          <div className="grid grid-cols-1 gap-4">
            <Card className="bg-card shadow-md">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <Trophy className="h-8 w-8 text-primary" />
                  <CardTitle className="text-lg">Tournament Management</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Create and manage tournaments with ease. Track standings, schedules, and results in real-time.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card shadow-md">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <CalendarDays className="h-8 w-8 text-primary" />
                  <CardTitle className="text-lg">Live Scoring</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Update scores ball-by-ball with an intuitive interface. Real-time updates for fans and officials.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card shadow-md">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <BarChart2 className="h-8 w-8 text-primary" />
                  <CardTitle className="text-lg">Detailed Statistics</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Access comprehensive statistics for players, teams, and tournaments. Analyze performance and track progress.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4 py-6">
          <Card className="bg-gradient-to-r from-cricket-green to-cricket-green-light text-white">
            <CardContent className="p-6 text-center">
              <h3 className="text-xl font-bold mb-3">Ready to Get Started?</h3>
              <p className="text-sm opacity-90 mb-4">
                Join thousands of cricket organizers using our platform
              </p>
              <Link to="/auth">
                <Button className="bg-white text-cricket-green hover:bg-gray-100">
                  Create Account
                </Button>
              </Link>
            </CardContent>
          </Card>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-cricket-green to-cricket-green-light text-white p-4 rounded-lg">
        <h1 className="text-xl font-bold mb-1">Welcome Back!</h1>
        <p className="text-sm opacity-90">Manage your cricket tournaments and matches</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        {stats.map((stat, index) => (
          <Link to={stat.path} key={index}>
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader className={`${stat.color} rounded-t-lg pb-2`}>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-sm">{stat.title}</CardTitle>
                  <stat.icon size={20} />
                </div>
              </CardHeader>
              <CardContent className="pt-3 text-center">
                <p className="text-2xl font-bold">{stat.value}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Live Matches */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Live Matches</CardTitle>
          <CardDescription>Currently ongoing cricket matches</CardDescription>
        </CardHeader>
        <CardContent>
          {matches.filter(m => m.status === 'live').length > 0 ? (
            <div className="space-y-3">
              {matches.filter(m => m.status === 'live').map(match => {
                const team1 = teams.find(t => t.id === match.team1Id);
                const team2 = teams.find(t => t.id === match.team2Id);
                return (
                  <div key={match.id} className="p-3 border rounded-lg hover:bg-accent/50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                        </span>
                        <h4 className="font-semibold text-sm">{team1?.name || 'Team 1'} vs {team2?.name || 'Team 2'}</h4>
                      </div>
                      <Link to={`/matches/${match.id}`}>
                        <Button variant="outline" size="sm">
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {team1?.name || 'Team 1'}: {match.team1Score.runs}/{match.team1Score.wickets} ({match.team1Score.overs}.{match.team1Score.balls})
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground text-sm">
              No live matches at the moment
            </div>
          )}
          <div className="mt-4">
            <Link to="/matches">
              <Button variant="outline" className="w-full">View All Matches</Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
          <CardDescription>Manage tournaments, teams, players and matches</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Link to="/tournaments/new" className="block">
            <Button className="w-full bg-cricket-green hover:bg-cricket-green-light justify-start">
              <Trophy className="h-4 w-4 mr-2" />
              Create Tournament
            </Button>
          </Link>
          <Link to="/teams/new" className="block" onClick={(e) => {
            if (tournaments.length === 0) {
              e.preventDefault();
              toast({
                title: "Cannot create teams yet",
                description: "Please create a tournament first.",
                variant: "destructive",
              });
            }
          }}>
            <Button className="w-full bg-cricket-green-light hover:bg-cricket-green justify-start">
              <Users className="h-4 w-4 mr-2" />
              Add Team
            </Button>
          </Link>
          <Link to="/players/new" className="block" onClick={(e) => {
            if (teams.length === 0) {
              e.preventDefault();
              toast({
                title: "Cannot create players yet",
                description: "Please create a team first.",
                variant: "destructive",
              });
            }
          }}>
            <Button className="w-full bg-cricket-boundary hover:opacity-90 justify-start">
              <User className="h-4 w-4 mr-2" />
              Add Player
            </Button>
          </Link>
          <Link to="/matches/new" className="block" onClick={(e) => {
            if (teams.length < 2) {
              e.preventDefault();
              toast({
                title: "Cannot schedule matches yet",
                description: "Please create at least two teams first.",
                variant: "destructive",
              });
            }
          }}>
            <Button className="w-full bg-cricket-accent hover:opacity-90 justify-start">
              <CalendarDays className="h-4 w-4 mr-2" />
              Schedule Match
            </Button>
          </Link>
          <Link to="/statistics" className="block">
            <Button variant="outline" className="w-full justify-start">
              <BarChart2 className="h-4 w-4 mr-2" />
              View Statistics
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default MobileHomePage; 